import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { API_BASE, ensureValidToken } from "@/lib/authStorage";
import { apiError } from "@/lib/api";

/**
 * SSE with polling fallback for operational screens (kitchen, cashier, tables).
 * @param {() => void | Promise<void>} onRefresh - called on events or poll tick
 * @param {{ pollMs?: number, enabled?: boolean }} options
 */
export function useOperationalSync(onRefresh, { pollMs = 8000, enabled = true } = {}) {
  const onRefreshRef = useRef(onRefresh);
  const pollRef = useRef(null);
  const esRef = useRef(null);
  const lastErrorToastRef = useRef(0);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const tick = useCallback(async () => {
    if (document.hidden) return;
    try {
      await onRefreshRef.current?.();
    } catch (e) {
      const now = Date.now();
      if (now - lastErrorToastRef.current > 30_000) {
        lastErrorToastRef.current = now;
        toast.error(apiError(e));
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    let closed = false;
    const jitter = Math.floor(Math.random() * 1500);
    const interval = Math.max(3000, pollMs) + jitter;

    const startPolling = () => {
      if (pollRef.current) return;
      tick();
      pollRef.current = setInterval(tick, interval);
    };

    const connectSse = async () => {
      const token = await ensureValidToken();
      if (!token || closed) {
        startPolling();
        return;
      }
      try {
        const es = new EventSource(`${API_BASE}/events/stream?token=${encodeURIComponent(token)}`);
        esRef.current = es;
        es.onmessage = () => tick();
        es.onerror = () => {
          es.close();
          esRef.current = null;
          startPolling();
        };
        tick();
      } catch {
        startPolling();
      }
    };

    connectSse();

    const onVis = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      closed = true;
      document.removeEventListener("visibilitychange", onVis);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [enabled, pollMs, tick]);
}
